/**
 * @module dropdownTrigger
 *
 * The resting appearance shared by `NativeSelect` and `Select`'s trigger.
 *
 * Checkout renders both alongside Stripe's `PaymentElement`, which paints a
 * native `<select>` we can't restyle, so all three controls have to agree
 * (DEV-6463). Both components pull their box and chevron classes from here
 * rather than keeping their own copies, so the two can't drift apart without
 * someone deliberately editing this file.
 *
 * Only the resting state lives here. Open states are each component's own
 * business: `Select` paints a Radix listbox, `NativeSelect` hands off to the
 * OS picker.
 */
import { cva } from "class-variance-authority";

import { inputVariants } from "./input";

/**
 * Box classes for a dropdown control. Built on `inputVariants` so dropdowns
 * stay in step with `Input` — the padding leaves room for the chevron.
 *
 * Deliberately no `relative`: the chevron's positioning ancestor differs
 * between the two consumers (a wrapper for `NativeSelect`, the trigger itself
 * for `Select`), so each adds it where it's actually needed.
 */
export const dropdownTriggerVariants = cva([inputVariants()], {
  variants: {
    size: {
      default: "h-10 pr-10 pl-4",
      sm: "h-7 pr-8 pl-3",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

/**
 * Chevron classes for a dropdown control. Absolutely positioned so the
 * affordance sits in the same place whether the text next to it is painted by
 * the browser (`NativeSelect`) or by a span (`Select`).
 */
export const dropdownChevronVariants = cva(
  "pointer-events-none absolute top-1/2 -translate-y-1/2 text-gray-950 dark:text-gray-300",
  {
    variants: {
      size: {
        default: "size-4",
        sm: "size-3",
      },
      /**
       * Which box the chevron is positioned against. Absolute offsets resolve
       * against the containing block's *padding* box, so a chevron inside the
       * bordered control sits 1px further in than one that's a sibling of it.
       * Without this the two components land a pixel apart.
       *
       * `bare`     sibling of the control, inside an unbordered wrapper
       *            (NativeSelect)
       * `bordered` a child of the bordered control itself (Select's trigger)
       */
      context: {
        bare: "",
        bordered: "",
      },
    },
    compoundVariants: [
      { size: "default", context: "bare", class: "right-4" },
      { size: "default", context: "bordered", class: "right-[calc(1rem-1px)]" },
      { size: "sm", context: "bare", class: "right-3" },
      { size: "sm", context: "bordered", class: "right-[calc(0.75rem-1px)]" },
    ],
    defaultVariants: {
      size: "default",
      context: "bare",
    },
  }
);
